import React, { Component } from "react";

import handleTouch from "../../../../../../common/helpers/handleTouch";

import player from "../../../../../player";
import Icons from "../../../../../icons";

import store from "../store";

import { getRhyt } from "./utils";
import { PushButton } from "./styles";
import { IProps, IState } from "./interfaces";

const accuracyLevels = [0, 0.38, 0.33, 0.28];

export class RhytRecording extends Component<IProps, IState> {
    rhyt: number[][] = []
    tied: boolean[][] = []
    current: { bar: number, note: number } = { bar: 1, note: 0 }
    bufferNode?: AudioBufferSourceNode;
    markTimer?: ReturnType<typeof setTimeout>;
    disabled = false;
    constructor(props: IProps) {
        super(props);
        this.state = {
            isContextRunning: player.context.state === "running",
        };
        if (!this.state.isContextRunning) {
            player.context.onstatechange = this.onStateChange;
        } else {
            this.setupSlap();
        }
        const { tied, rhyt } = getRhyt(props.score);
        this.rhyt = rhyt;
        this.tied = tied;
        this.current = { bar: rhyt[0].length === 0 ? 1 : 0, note: 0 };
        window.addEventListener("keydown", this.recordEvent);
        window.addEventListener("keyup", this.keyUp);
    }
    onStateChange = () => {
        this.setState({ isContextRunning: player.context.state === "running" }, () => {
            if (this.state.isContextRunning) {
                this.setupSlap();
            }
        });
    };
    setupSlap = async () => {
        if (!player.slap) {
            await player.loadSlap();
        }
        this.bufferNode = player.slap!.createNode(undefined);
    };
    isTouch = false;
    componentWillUnmount() {
        window.removeEventListener("keydown", this.recordEvent);
        window.removeEventListener("keyup", this.keyUp);
        clearTimeout(this.markTimer);
    }
    keyOn = true;
    recordTimes: number[][] = [];
    keyUp = (e: KeyboardEvent) => {
        if (e.keyCode === 32) this.keyOn = true;
    };
    recordEvent = (e: KeyboardEvent | React.TouchEvent | React.MouseEvent | null) => {
        if (this.props.playing) return;
        if (this.isTouch && e && e.type === "mousedown") return;
        if (!this.state.isContextRunning) return;
        if (this.disabled) return;
        if (this.props.preview) return;
        if (e) {
            if (e.type === "touchstart") this.isTouch = true;
            if (e.type === "keydown") {
                if ((e as KeyboardEvent).keyCode !== 32) {
                    return;
                }
                e.preventDefault();
            }
            handleTouch(e as KeyboardEvent);
            if (!this.keyOn) return;
            if (e.type === "keydown") {
                this.keyOn = false;
            }
        }
        const echoSound = store.get();
        if (echoSound && e && player.slap) {
            if (!this.bufferNode) {
                this.bufferNode = player.slap!.createNode(undefined);
            }
            this.bufferNode.start(player.context.currentTime, 0.1);
            this.bufferNode = player.slap!.createNode(undefined);
        }
        const { recordTimes, current, rhyt } = this;
        if (recordTimes.length === 0) {
            current.bar = rhyt[0].length === 0 ? 1 : 0;
            current.note = 0;
            while (rhyt[current.bar] && (rhyt[current.bar][current.note] < 0 || !rhyt[current.bar][current.note])) {
                if (!rhyt[current.bar][current.note]) {
                    current.bar++;
                    current.note = 0;
                } else {
                    current.note++;
                }
            }
        }
        let nextBar = current.bar;
        let isLast = true;
        let nextNum = current.note;
        if (!rhyt[nextBar]) {
            return;
        }
        for (let g = current.note; g < rhyt[nextBar].length; g++) {
            if ((recordTimes[nextBar] && recordTimes[nextBar][g]) || (rhyt[nextBar][g] < 0 && e)) {
                nextNum++;
                continue;
            }
            if (rhyt[nextBar][g]) {
                isLast = false;
                break;
            }
        }
        if (isLast) {
            nextBar++;
            nextNum = 0;
            if (e) {
                while (rhyt[nextBar] && rhyt[nextBar][nextNum] < 0) {
                    nextNum++;
                }
            }
        }
        this.record(nextBar, nextNum);
    };
    record = (bar: number, note: number) => {
        const { recordTimes, current, rhyt } = this;
        let c;
        
        if (rhyt[bar] && rhyt[bar][note] === undefined) {
            return;
        }
        current.bar = Number(bar);
        current.note = Number(note);
        if (recordTimes[bar] === undefined) recordTimes[bar] = [];

        clearTimeout(this.markTimer);

        recordTimes[bar][note] = player.context.currentTime;
        this.props.setActiveNote(current);

        let lastNoteOfBar = 0;
        for (c = 0; c < rhyt[rhyt.length - 1].length; c++) {
            if (rhyt[rhyt.length - 1][c] >= 0) lastNoteOfBar = c;
        }
        let isLastNote = false;
        if (bar === rhyt.length - 1)
            for (let z = lastNoteOfBar; z >= note; z--) {
                if (z === note) isLastNote = true;
                if (rhyt[rhyt.length - 1][z] > 0) {
                    break;
                }
            }

        if (bar === rhyt.length - 1 && (note === lastNoteOfBar || isLastNote)) {
            this.itIsDone();
        } else {
            this.notDoneYet(bar, note);
        }
    };
    itIsDone = () => {
        setTimeout(() => {
            this.props.setActiveNote(null);
        }, 600);
        const { rhyt, recordTimes } = this;
        const { accuracy, tempo } = this.props;
        const orderedRecord = [];
        for (let b = 0; b < rhyt.length; b++) {
            for (let n = 0; n < rhyt[b].length; n++) {
                if (rhyt[b][n] < 0) continue;
                orderedRecord.push(recordTimes[b][n]);
            }
        }

        const times = [];
        const ratio = [];
        const orderedRhyt = [];
        for (let b = 0; b < rhyt.length; b++) {
            for (let n = 0; n < rhyt[b].length; n++) {
                if (rhyt[b][n] < 0) {
                    orderedRhyt[orderedRhyt.length - 1] += Math.abs(rhyt[b][n]);
                    continue;
                }
                orderedRhyt.push(Math.abs(rhyt[b][n]));
            }
        }
        for (let r = 1; r < orderedRhyt.length; r++) {
            times.push(orderedRecord[r] - orderedRecord[r - 1]);
            ratio.push(times[r - 1] / orderedRhyt[r - 1]);
        }
        const min = Math.min.apply(null, ratio);
        const max = Math.max.apply(null, ratio);
        const dif = max - min;

        let average = 0;
        let t;
        for (t = 0; t < ratio.length; t++) {
            average += ratio[t];
        }
        average /= t;
        const badRhyts = [];

        for (t = 0; t < ratio.length; t++) {
            if (
                (ratio[t] > average + accuracyLevels[accuracy] / 2) ||
                (ratio[t] < average - accuracyLevels[accuracy] / 2)
            ) {
                badRhyts.push(t);
            }
        }

        for (t = badRhyts.length - 2; t >= 0; t--) {
            if (badRhyts[t] + 1 === badRhyts[t + 1] && badRhyts[t - 1] !== badRhyts[t] - 1) {
                badRhyts.splice(t, 1);
            }
        }
        const recordTempo = 60 / average;
        const passed = dif < accuracyLevels[accuracy]
        let issue = "";

        if (!passed) {
            this.disabled = true;

            let c = 0;
            let current = 0;
            const badBoxes: { bar: number, note: number }[] = [];
            for (let bar = 0; bar < rhyt.length; bar++) {
                for (let note = 0; note < rhyt[bar].length; note++) {
                    if (rhyt[bar][note] > 0) {
                        if (c === badRhyts[current]) {
                            current++;
                            badBoxes.push({ bar, note });
                        }
                        c++;
                    }
                }
            }
            setTimeout(() => {
                this.props.mistake(issue, badBoxes);
                this.recordTimes = [];
            }, 600);
        } else {
            let tooFast = false;
            let tooSlow = false;

            if (recordTempo > tempo * 1.4) {
                tooFast = true;
            } else if (recordTempo < tempo * 0.6) {
                tooSlow = true;
            }

            if (tooFast || tooSlow) {
                this.disabled = true;
                issue = "fast";
                if (tooSlow) issue = "slow";
                setTimeout(() => {
                    this.props.mistake(issue);
                    this.recordTimes = [];
                }, 600);
            } else {
                this.disabled = true;
                setTimeout(() => {
                    this.props.completed();
                }, 600);
            }
        }
    }
    notDoneYet = (bar: number, note: number) => {
        const { recordTimes, rhyt, tied } = this;
        let next: number;
        if (!rhyt[bar]) {
            return;
        }
        if (!rhyt[bar][note + 1]) {
            if (!rhyt[bar + 1]) {
                return;
            }
            next = rhyt[bar + 1][0];
        } else {
            next = rhyt[bar][note + 1];
        }

        let isTied = tied[bar][note];

        let a1: number = 0;
        let prevAdded = false;
        if (note - 1 >= 0) {
            a1 = Math.abs(rhyt[bar][note - 1]);
        } else if (bar > 0) {
            prevAdded = true;
            a1 = Math.abs(rhyt[bar - 1][rhyt[bar - 1].length - 1]);
        }
        if (a1 < 0) {
            a1 = Math.abs(a1);
            let a1b = 1;
            let a1break = false;
            if (note - 1 >= 0) {
                while (note - a1b >= 0) {
                    if (rhyt[bar][note - a1b] < 0) {
                        a1 += Math.abs(rhyt[bar][note - a1b]);
                    } else {
                        a1break = true;
                        break;
                    }
                    a1b++;
                }
            }
            if (!a1break && bar - 1 >= 0) {
                const bp = rhyt[bar - 1].length - 1;
                a1b = 0;

                if (prevAdded) a1b = 1;
                while (bp - a1b >= 0) {
                    if (rhyt[bar - 1][bp - a1b] < 0) {
                        a1 += Math.abs(rhyt[bar - 1][bp - a1b]);
                    } else {
                        a1 += Math.abs(rhyt[bar - 1][bp - a1b]);
                        break;
                    }
                    a1b++;
                }
            }
        }

        const b1 = rhyt[bar][note];
        const rT = [];
        for (let ta = 0; ta < recordTimes.length; ta++) {
            if (!recordTimes[ta]) continue;
            for (let tb = 0; tb < recordTimes[ta].length; tb++) {
                if (recordTimes[ta][tb]) {
                    rT.push(recordTimes[ta][tb]);
                }
            }
        }
        const a2 = rT[rT.length - 1] - rT[rT.length - 2];
        const b2 = (a2 / a1) * Math.abs(b1);
        if (next < 0 || isTied) {
            if (bar === this.props.firstBar && note === 0) {
                this.markTimer = setTimeout(() => {
                    this.recordEvent(null);
                }, ((Math.abs(rhyt[bar][note]) * 60) / this.props.tempo) * 1000);
            } else {
                if (isTied) {
                    this.markTimer = setTimeout(() => {
                        this.recordEvent(null);
                    }, b2 * 1000);
                } else
                    this.markTimer = setTimeout(() => {
                        this.recordEvent(null);
                    }, b2 * 1000);
            }
        } else {
            const nextEstTime = b2 * 1000;
            if (!isNaN(nextEstTime)) {
                this.markTimer = setTimeout(() => {
                    this.timeUp();
                }, b2 * 1000 * 3);
            }
        }
    };
    timeUp = () => {
        this.props.mistake("timeup");
    };
    render() {
        const { isContextRunning } = this.state;
        const { isLow, playing, isEaster } = this.props;
        return (
            <>
                {isContextRunning && (
                    <PushButton
                        isEaster={isEaster}
                        className="in"
                        onMouseDown={(e: React.MouseEvent) => this.recordEvent(e)}
                        onTouchStart={(e: React.TouchEvent) => this.recordEvent(e)}
                        isLow={isLow}
                        disabled={playing}
                    >
                        <Icons.rhytKnock />
                    </PushButton>
                )}
            </>
        );
    }
}

export default RhytRecording;
